import { z } from 'zod';

import type { AstInput } from '../normalize.ts';

/**
 * Codec API for AST-shaped op arguments.
 *
 * Some op schemas declare a custom slot whose shape the generic Zod
 * walkers (`relaxSymbols`, the doc-walker `typeLabel`) cannot infer
 * from `def.type` alone:
 *
 * **Opaque AST slots** (`astArg` / `filterPredicateArg` /
 * `columnVectorArg`) — the form holds the raw normalised AST verbatim
 * (a `'special'` op evaluates it on its own terms; the Zod parse is a
 * pass-through). `relaxSymbols` must NOT descend into these slots —
 * the AST already encodes its own symbols structurally, and rewriting
 * would corrupt the tree.
 *
 * Two brand WeakSets track these slots:
 *
 *   AST_CODECS     ⊃ astArg / filterPredicateArg / columnVectorArg products — `walk` skips
 *   LABELED_CODECS ⊃ all factories — `typeLabel` renders via `describe`
 */
const AST_CODECS = new WeakSet<object>();

/** Branded so `typeLabel` renders the slot via its `.describe()` text. */
const LABELED_CODECS = new WeakSet<object>();

/**
 * Identity codec for an opaque AST slot. Used by `'special'` ops whose
 * apply walks the raw AST itself (`case` clauses, `def` body, `(col …)`
 * Form 1 path).
 *
 * The codec is registered in both brand sets: `relaxSymbols` skips it
 * (opaque) and `typeLabel` renders the `label` (default `<expr>`).
 */
export const astArg = (label = '<expr>'): z.ZodType<AstInput> => {
  const codec = z
    .codec(z.unknown(), z.unknown(), {
      decode: (raw) => raw,
      encode: (out) => out,
    })
    .describe(label) as unknown as z.ZodType<AstInput>;
  AST_CODECS.add(codec);
  LABELED_CODECS.add(codec);
  return codec;
};

/**
 * Single source of truth for the lambda-shaped predicate slot used in
 * filter contexts — the second arg of `(filter ds <fn>)` and the
 * `(filter #(…))` pipeline form of the `query` source op. Both consume
 * identical syntax (`#(predicate-body)` reader macro desugared to
 * `(fn [%] body)`) and route through `extractLambdaBody` to recover the
 * predicate expression for col-ref enumeration and CDF push-down.
 */
export const filterPredicateArg = (): z.ZodType<AstInput> => astArg('<fn>');

/**
 * Single source of truth for the column-vector slot used in
 * column-list contexts — the second arg of `(columns ds [:a :b])` /
 * `(distinct ds [:a :b])` and the `(columns [:a :b])` pipeline form of
 * the `query` source op. Decoded at apply time via `columnNameAstList`.
 */
export const columnVectorArg = (): z.ZodType<AstInput> => astArg('<columns>');

/** True for codecs whose AST contents are opaque (no sym-codec walk). */
export const isAstCodec = (s: z.ZodType): boolean => AST_CODECS.has(s);

/** True for codecs whose `describe` text should be the rendered label. */
export const isLabeledCodec = (s: z.ZodType): boolean => LABELED_CODECS.has(s);
